import styled, { css } from 'styled-components';

import * as mx from 'core/styles/mixins';
import * as vr from 'core/styles/variables';
import { Content } from 'pods/forms/components/base/Label/Label.style';

export const RadioCheckboxItem = styled.li<{
	hasError: boolean;
	hasBorderline: boolean;
}>`
	&::before {
		display: none;
	}

	${Content} {
		padding: 0;
		margin: 0;
	}

	input {
		/* Shared between radio and checkbox */
		&[type='radio'],
		&[type='checkbox'] {
			${mx.visuallyhidden};

			+ label {
				position: relative;
				display: block;
				padding: 5px 20px;
				padding-left: 30px;
				line-height: 1.5;

				&.-hidden {
					> span {
						position: absolute;
						width: 1px;
						height: 1px;
						padding: 0;
						margin: -1px;
						overflow: hidden;
						clip: rect(0, 0, 0, 0);
						border: 0;
					}
				}

				${props =>
					props.hasBorderline
						? css`
								padding-left: 20px;
						  `
						: css`
								padding-left: 30px;
						  `}

				span {
					font-weight: 400;
				}

				&::before,
				&::after {
					${mx.pseudo}
				}

				&::before {
					height: 20px;
					width: 20px;
					left: 0;
					top: 7px;
					background-color: ${vr.brandWhite};

					${props =>
						props.hasBorderline
							? css`
									border: unset;
							  `
							: css`
									border: 1px solid ${vr.radioCheckboxDefault};
							  `}
				}

				&::after {
					opacity: 0;
					transition: opacity ${vr.baseTransition};
				}
			}

			&:hover:not(:disabled) + label {
				&::before {
					border-color: ${({ theme }) => theme.global.form.radio.active};
				}
			}

			&:focus + label {
				&::before {
					${mx.mimicFocus}
				}
			}

			&:disabled + label {
				cursor: default;

				&::before,
				&::after {
					opacity: 0.5;
				}
			}
		}

		&[type='select'] {
			${({ hasError }) =>
				hasError &&
				css`
					.formBuilder__select_control {
						border: 1px solid ${vr.formfieldErrorColor};
					}
				`}
		}

		/* Radio button */
		&[type='radio'] {
			+ label {
				&::before {
					border-radius: 25px;
				}

				&::after {
					top: 12px;
					left: 5px;
					height: 10px;
					width: 10px;
					border-radius: 25px;
				}
			}

			&:checked {
				& + label {
					${props =>
						props.hasBorderline
							? css`
									border: 1.5px solid
										${({ theme }) => theme.global.form.radio.active};
							  `
							: css``}

					&::before {
						border-color: ${({ theme }) => theme.global.form.radio.active};
					}

					&::after {
						background-color: ${({ theme }) => theme.global.form.radio.active};
						opacity: ${props => (props.hasBorderline ? `0` : `1`)};
					}
				}

				&:disabled,
				&[readonly] {
					& + label {
						&::before {
							border-color: ${vr.radioCheckboxDisabled};
						}

						&::after {
							background-color: ${vr.radioCheckboxDisabled};
						}
					}
				}

				&:disabled + label::after {
					background-color: lighten(0.2, ${vr.radioCheckboxDisabled});
				}
			}
		}

		/* Checkbox */
		&[type='checkbox'] {
			+ label {
				&::before {
					background-color: none;
					transition: background-color ${vr.baseTransition};
				}

				&::after {
					top: 10px;
					left: 7px;
				}
			}

			&:checked {
				& + label {
					&::before {
						border-color: ${({ theme }) => theme.global.form.radio.active};
						background-color: ${vr.brandWhite};
					}

					&::after {
						${mx.tick}
						opacity: 1;
					}
				}

				&:disabled,
				&[readonly] {
					& + label {
						&::before {
							background-color: ${vr.radioCheckboxDisabled};
							border-color: ${vr.radioCheckboxDisabled};
						}
					}
				}
			}
		}
	}

	label {
		padding-left: 12px;
		margin: 0;
		cursor: pointer;
	}
`;

export const RadioCheckboxList = styled.ul<{
	hasError: boolean;
	isSwitch: boolean;
	isHorizontal: boolean;
	hasBorderline: boolean;
}>`
	${mx.listReset}

	> li {
		margin-top: 0;

		&:only-child {
			margin-left: 0;
		}

		&:last-of-type {
			margin-bottom: 20px;
		}
	}

	${({ isSwitch, isHorizontal, hasBorderline, theme }) =>
		(isSwitch || isHorizontal) &&
		css`
			display: flex;
			flex-wrap: wrap;

			/* TODO does this work...? */
			${RadioCheckboxItem} {
				&:not(:last-child) {
					margin-right: 0;
				}

				label {
					border: ${hasBorderline
						? `1px solid ${theme.global.borders.faded}`
						: `none`};
				}
			}
		`}

	${({ isSwitch, hasError, theme }) =>
		isSwitch &&
		css`
			/* To offset bottom margin of 'RadioCheckboxItem' elements */
			margin-bottom: -10px;

			li {
				min-width: 75px;
			}

			${RadioCheckboxItem} {
				margin: 0 10px 10px 0;

				&:last-of-type input[type='radio'] + label {
					margin-right: 0;
					border-radius: 0 3px 3px 0;
				}

				&:not(:last-of-type) input[type='radio'] + label {
					margin-right: -1px;
					border-radius: 3px 0 0 3px;
				}

				input[type='radio'] {
					+ label {
						position: initial;
						padding: 12px 16px;
						display: block;
						border: 1px solid #66696d;
						text-align: center;
						min-width: 75px;
						background-color: ${vr.brandWhite};
						line-height: 1.4;

						${() =>
							hasError &&
							css`
								border: 1px solid ${vr.formfieldErrorColor} !important;
							`}

						&::before,
					&::after {
							content: none;
						}
					}

					&:not(:checked):not(:disabled):not([readonly]) + label:hover {
						background-color: ${theme.global.form.radio.hover};
					}

					&:disabled,
					&[readonly] {
						+ label {
							background-color: ${vr.formfieldDisabledBackground};
							border-color: ${vr.radioCheckboxDefault};
						}
					}

					&:checked {
						& + label {
							background-color: ${theme.global.form.radio.active};
							color: ${vr.brandWhite};
						}

						&:disabled,
						&[readonly] {
							+ label {
								border-color: ${vr.radioCheckboxDisabled};
								background-color: ${vr.radioCheckboxDisabled};
							}
						}
					}

					&:focus + label {
						${mx.mimicFocus}
					}
				}
			}
		`}
`;

// TODO Radio checkbox specific error styles
// .ctrlHolder {
// 	&.-radioCheckbox {
// 		margin-bottom: 15px;
// 		display: inline-block;
// 	}

// 	&.-error .o-radioCheckbox {
// 		.ctrlHolder__label {
// 			color: $formfield-error-color;
// 		}

// 		&.-switch .ctrlHolder__label,
// 		.ctrlHolder__label::before {
// 			border: 2px solid $formfield-error-border-color;
// 		}

// 		&.-switch .ctrlHolder__label {
// 			padding: 4px 14px; // To offset new border-width
// 		}
// 	}
// }
